import { HttpEvent } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { GetTimeFromTimestamp,GetTimestampFromTime } from '@dave/types';
import { Store } from '@ngrx/store';
import * as moment from 'moment';
import { combineLatest,distinctUntilChanged,filter,map,Observable,of,switchMap,take,tap } from 'rxjs';
import { catchError,shareReplay } from 'rxjs/operators';
import { isNotNullOrUndefined,stringifyIfNotNullOrUndefined,stringifyIfNotUndefined } from '../../helper/helper';
import { EnumClockInTypeSlug } from '../entities/clock-in-type.entity';
import { ClockInEntity,ClockInEntityFromBackend,ClockInEntityGqlFields } from '../entities/clock-in.entity';
import { EmployeeSalaryTypeEntity } from '../entities/employee-salary-type.entity';
import { EmployeeToVacationEntity } from '../entities/employee-to-vacation.entity';
import { PartnerTypeEnum } from '../entities/partner.entity';
import { WorkDayEntity } from '../entities/work-day.entity';
import { DaveMutation,DaveMutationChangeClockInArgs,DaveMutationCreateAndDeleteMultipleClockInsArgs,DaveMutationCreateClockInArgs,DaveMutationDeleteClockInArgs,Query } from '../graphql-types';
import { CustomerResolver } from '../guards/customer.resolver';
import { WorkDayResolver } from '../guards/work-day.resolver';
import { State } from '../State';
import { BaseActionTypes } from '../State/actions/base.actions';
import { UsersActionTypes } from '../State/actions/users.actions';
import { ClockInErrorCodes } from '../State/effects/clock-in.effects';
import { getEmployeeByUserId } from '../State/selectors/employees.selectors';
import { getPartner } from '../State/selectors/partners.selectors';
import { getSetting,getUser } from '../State/selectors/users.selectors';
import { AppGatewayService } from './app-gateway.service';
import { HttpService } from './http.service';

// enum ErrorCodes {
//     Add = 'Buchungsposition hinzufügen fehlgeschlagen',
//     Modify = 'Buchungsposition ändern fehlgeschlagen',
//     Delete = 'Buchungsposition löschen fehlgeschlagen',
//     Load = 'Buchungsposition Abrufen fehlgeschlagen',
// }

export interface HourListData {
    HasInvalid: boolean;
    Date: Date;
    WorkTime: number;
    ProjectTime: number;
    OverTime: number;
    BreakTime: number;
    ForcedBreakTime: number;
    DrivingTime: number;
    SomeExported: boolean;
    CanDeleteAll: boolean;
    IsHoliday: boolean;
    CommissionIds: number[];
    CustomerIds: number[];
    WorkdayIds: number[];
    EmployeeToVacationsIds: number[];
}
const stringifyCreatePayload = (payload: DaveMutationCreateClockInArgs) => {
    return `${stringifyIfNotNullOrUndefined(payload, 'timeStamp')}
            ${stringifyIfNotNullOrUndefined(payload, 'commissionId')}
            ${stringifyIfNotNullOrUndefined(payload, 'userId')}
            ${stringifyIfNotNullOrUndefined(payload, 'employeeId')}
            ${stringifyIfNotNullOrUndefined(payload, 'clockInTypeId')}
            ${stringifyIfNotNullOrUndefined(payload, 'type')}
            ${stringifyIfNotNullOrUndefined(payload, 'city')}
            ${stringifyIfNotNullOrUndefined(payload, 'street')}
            ${stringifyIfNotNullOrUndefined(payload, 'postalCode')}
            ${stringifyIfNotNullOrUndefined(payload, 'country')}
            ${stringifyIfNotNullOrUndefined(payload, 'workDescription')}
            ${stringifyIfNotNullOrUndefined(payload, 'drivingTime')}
            ${stringifyIfNotNullOrUndefined(payload, 'autoAssignCommissionId')}`;
};
@Injectable({
    providedIn: 'root',
})
export class ClockInDataService {
    constructor(private store: Store<State>, private gatewayHttpService: HttpService, private appGatewayService: AppGatewayService, protected customerResolver: CustomerResolver, protected workDayResolver: WorkDayResolver) {}

    ClockInsExits(employeeId: number, from: Date, to: Date): Promise<boolean> {
        return new Promise((resolve, reject) => {
            resolve(false);
        });
    }

    GetHourListData(year: number, quarter: number | null, commissionIds: number[], employeeIds: number[]): Promise<{data: HourListData[], from: Date, to: Date}> {
        let fromDate = moment().year(year).startOf('year').toDate();
        let toDate = moment(fromDate).endOf('year').toDate();
        if (quarter !== null) {
            fromDate = moment().year(year).quarter(quarter).startOf('quarter').toDate();
            toDate = moment(fromDate).endOf('quarter').toDate();
        }

        return new Promise((resolve, reject) => {
            this.appGatewayService
                .Request({
                    BaseGetHourList: {
                        FromDate: GetTimestampFromTime(fromDate).toString(),
                        ToDate: GetTimestampFromTime(toDate).toString(),
                        CommissionIds: commissionIds.map(c => c.toString()),
                        EmployeeIds: employeeIds.map(e => e.toString()),
                    },
                })
                .then((res) => {
                    let out: HourListData[] = [];
                    res.BaseGetHourList.HourListDays.forEach((e) => {
                        out.push({
                            Date: GetTimeFromTimestamp(+e.Date),
                            BreakTime: +e.BreakTimeSum,
                            CommissionIds: e.CommissionIds.map((c) => +c),
                            CustomerIds: e.CustomerIds.map((c) => +c),
                            DrivingTime: +e.DrivingTimeSum,
                            EmployeeToVacationsIds: e.EmployeeToVacationIds.map((id) => +id),
                            ForcedBreakTime: +e.ForcedBreakTimeSum,
                            HasInvalid: e.HasInvalidWorkday,
                            OverTime: +e.OverTimeSum,
                            ProjectTime: +e.ProjectTimeSum,
                            WorkTime: +e.WorkTimeSum,
                            SomeExported: e.SomeExported,
                            WorkdayIds: e.WorkdayIds.map(id => +id),
                            CanDeleteAll: false,
                            IsHoliday: false,
                        });
                    });
                    resolve({
                        data: out,
                        from: GetTimeFromTimestamp(+res.BaseGetHourList.MinDate),
                        to: GetTimeFromTimestamp(+res.BaseGetHourList.MaxDate),
                    });
                });
        });
    }

    private userSettingsForClockIns$ = combineLatest([
        this.store.select(getSetting),
        this.store.select(getUser).pipe(
            switchMap((u) => this.store.select(getEmployeeByUserId({ userId: u.Id }))),
            filter(isNotNullOrUndefined),
        ),
        this.store.select(getPartner),
    ]).pipe(
        tap(([settings, employee, partner]) => {
            if (settings?.ShowTimeTrackingClockWatch === undefined && settings?.ShowTimeTrackingPopup === undefined) {
                let newSettings = Object.assign({}, settings);
                if (partner.PartnerTypeId === PartnerTypeEnum.Kagetec && [10, 11].includes(employee?.JobSpecificationId)) {
                    newSettings.ShowTimeTrackingClockWatch = false;
                    newSettings.ShowTimeTrackingPopup = true;
                } else {
                    newSettings.ShowTimeTrackingClockWatch = true;
                    newSettings.ShowTimeTrackingPopup = false;
                }
                settings = newSettings;
                this.store.dispatch(
                    UsersActionTypes.UpdateUserSettings({
                        Payload: settings,
                    }),
                );
            }
        }),
        map(([settings]) => settings),
        distinctUntilChanged(),
        shareReplay({ refCount: true, bufferSize: 1 }),
    );
    createClockIn(payload: DaveMutationCreateClockInArgs): Observable<Query & DaveMutation>;
    createClockIn(payload: DaveMutationCreateClockInArgs, withProgress: true): Observable<HttpEvent<Object>>;
    createClockIn(payload: DaveMutationCreateClockInArgs, withProgress: boolean = false) {
        const queryString = `
        mutation {
            createClockIn(
                ${stringifyCreatePayload(payload)}
            ) {
                ${ClockInEntityGqlFields.join(',')}
          }
        }`;
        return withProgress ? this.gatewayHttpService.graphQlWithProgress({ query: queryString }) : this.gatewayHttpService.graphQl({ query: queryString });
    }
    changeClockIn(payload: DaveMutationChangeClockInArgs): Observable<Query & DaveMutation>;
    changeClockIn(payload: DaveMutationChangeClockInArgs, withProgress: true): Observable<HttpEvent<Object>>;
    changeClockIn(payload: DaveMutationChangeClockInArgs, withProgress: boolean = false) {
        const queryString = `
        mutation {
            changeClockIn(
                id: ${JSON.stringify(payload.id)}
                ${stringifyIfNotNullOrUndefined(payload, 'timeStamp')}
                ${stringifyIfNotUndefined(payload, 'commissionId')}
                ${stringifyIfNotNullOrUndefined(payload, 'userId')}
                ${stringifyIfNotNullOrUndefined(payload, 'employeeId')}
                ${stringifyIfNotNullOrUndefined(payload, 'clockInTypeId')}
                ${stringifyIfNotNullOrUndefined(payload, 'type')}
                ${stringifyIfNotNullOrUndefined(payload, 'city')}
                ${stringifyIfNotNullOrUndefined(payload, 'street')}
                ${stringifyIfNotNullOrUndefined(payload, 'postalCode')}
                ${stringifyIfNotNullOrUndefined(payload, 'country')}
                ${stringifyIfNotNullOrUndefined(payload, 'workDescription')}
                ${stringifyIfNotNullOrUndefined(payload, 'kostenstelle')}
                ${stringifyIfNotNullOrUndefined(payload, 'sachkonto')}
                ${stringifyIfNotNullOrUndefined(payload, 'bearbeitungsschluessel')}
                ${stringifyIfNotNullOrUndefined(payload, 'drivingTime')}
            ) {
                ${ClockInEntityGqlFields.join(',')}
          }
        }`;
        return withProgress ? this.gatewayHttpService.graphQlWithProgress({ query: queryString }) : this.gatewayHttpService.graphQl({ query: queryString });
    }
    deleteClockIn(payload: DaveMutationDeleteClockInArgs): Observable<Query & DaveMutation>;
    deleteClockIn(payload: DaveMutationDeleteClockInArgs, withProgress: true): Observable<HttpEvent<Object>>;
    deleteClockIn(payload: DaveMutationDeleteClockInArgs, withProgress: boolean = false) {
        const queryString = `
        mutation {
            deleteClockIn(
                id: ${JSON.stringify(payload.id)}
            ) {
                ${ClockInEntityGqlFields.join(',')}
          }
        }`;
        return withProgress ? this.gatewayHttpService.graphQlWithProgress({ query: queryString }) : this.gatewayHttpService.graphQl({ query: queryString });
    }
    createAndDeleteMultiple(payload: DaveMutationCreateAndDeleteMultipleClockInsArgs): Observable<Query & DaveMutation>;
    createAndDeleteMultiple(payload: DaveMutationCreateAndDeleteMultipleClockInsArgs, withProgress: true): Observable<HttpEvent<Object>>;
    createAndDeleteMultiple(payload: DaveMutationCreateAndDeleteMultipleClockInsArgs, withProgress: boolean = false) {
        const queryString = `
        mutation {
            createAndDeleteMultipleClockIns(
            posts: [
                ${payload.posts.map((post) => '{' + stringifyCreatePayload(post) + '}').join(',\n')}
            ],
            deleteIds: ${JSON.stringify(payload.deleteIds)}
            ) {
                ${ClockInEntityGqlFields.join(',')}
          }
        }`;
        return withProgress ? this.gatewayHttpService.graphQlWithProgress({ query: queryString }) : this.gatewayHttpService.graphQl({ query: queryString });
    }

    getUserSettingsForClockIns$() {
        return this.userSettingsForClockIns$;
    }
    getByWorkdays$(workdayIds: number[]) {
        return combineLatest(workdayIds.map((workDayId) => this.get$({ workDayId }))).pipe(
            map((results) => results.flat()),
            catchError((err, caught) => {
                this.store.dispatch(
                    BaseActionTypes.ErrorAction({
                        Payload: {
                            ToasterMessage: ClockInErrorCodes.Load,
                            Err: err,
                            Caught: caught,
                        },
                    }),
                );
                return of(null);
            }),
        );
    }
    getByCommissions$(commissionIds: number[]) {
        return combineLatest(commissionIds.map((commissionId) => this.get$({ commissionId }))).pipe(
            map((results) => results.flat()),
            catchError((err, caught) => {
                this.store.dispatch(
                    BaseActionTypes.ErrorAction({
                        Payload: {
                            ToasterMessage: ClockInErrorCodes.Load,
                            Err: err,
                            Caught: caught,
                        },
                    }),
                );
                return of(null);
            }),
        );
    }
    get$(payload: { commissionId?: number; workDayId?: number; employeeId?: number; timeStamp?: string; workDayDate?: string; }) {
        return this.gatewayHttpService
            .graphQl({
                query: `query {clockIn(
                ${stringifyIfNotUndefined(payload, 'workDayId')}
                ${stringifyIfNotUndefined(payload, 'commissionId')}
                ${stringifyIfNotUndefined(payload, 'employeeId')}
                ${stringifyIfNotUndefined(payload, 'timeStamp')}
                ${stringifyIfNotUndefined(payload, 'workDayDate')}
                ) {${ClockInEntityGqlFields.join(',')}}}`,
            })
            .pipe(
                map((result) => result.clockIn.map((ci) => ClockInEntityFromBackend(ci))),
                catchError((err, caught) => {
                    this.store.dispatch(
                        BaseActionTypes.ErrorAction({
                            Payload: {
                                ToasterMessage: ClockInErrorCodes.Load,
                                Err: err,
                                Caught: caught,
                            },
                        }),
                    );
                    return of(null);
                }),
            );
    }
}
